import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  BottomSheet,
  Button,
  Dialog,
  Icon,
  List,
  ListItem,
  Segmented,
  iconChevron,
  useSnackbar,
} from '@sauda/ui/kit';
import { useAuth } from '../auth/AuthContext.js';
import { LANGUAGES, readLanguage, setLanguage, type Language } from '../i18n.js';
import { api } from '../lib/api.js';
import type { Destination } from './nav.js';
import { applyThemeMode, readThemeMode, saveThemeMode, type ThemeMode } from './theme.js';

/** The account sheet: who you are, other agencies, links a role has no tab for, theme, devices, sign out. */
export function AccountSheet({
  open,
  onClose,
  extras,
}: {
  open: boolean;
  onClose: () => void;
  extras: Destination[];
}) {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const [mode, setMode] = useState<ThemeMode>(readThemeMode);
  const [lang, setLang] = useState<Language>(readLanguage);
  const [confirmOut, setConfirmOut] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);

  const switchTo = async (agencyId: string, name: string) => {
    setSwitching(agencyId);
    try {
      const result = await api<{ accessToken: string }>('/auth/switch-agency', {
        method: 'POST',
        body: { agencyId },
      });
      await auth.adopt(result.accessToken);
      await cache.invalidateQueries();
      onClose();
      void navigate('/', { replace: true });
      snack({ text: t('shell.nowIn', { agency: name }) });
    } finally {
      setSwitching(null);
    }
  };

  return (
    <>
      <BottomSheet
        open={open}
        onClose={onClose}
        label={t('shell.account')}
        title={t('shell.account')}
        closeLabel={t('app.close')}
      >
        {auth.current === null ? null : (
          <p className="k-t-body-lg">
            <strong>{auth.current.agencyName}</strong>
            <br />
            <span className="k-muted">{t(`roles.${auth.current.role}`)}</span>
          </p>
        )}
        {auth.memberships.length > 1 ? (
          <section aria-label={t('shell.switchAgency')} className="sheet-section">
            <h3 className="k-t-label k-muted">{t('shell.switchAgency')}</h3>
            <List>
              {auth.memberships.map((m) => (
                <ListItem
                  key={m.agencyId}
                  title={m.agencyName}
                  supporting={t(`roles.${m.role}`)}
                  selected={m.agencyId === auth.agencyId}
                  onClick={
                    m.agencyId === auth.agencyId
                      ? undefined
                      : () => void switchTo(m.agencyId, m.agencyName)
                  }
                  trailing={
                    switching === m.agencyId
                      ? t('app.loading')
                      : m.agencyId === auth.agencyId
                        ? t('shell.current')
                        : undefined
                  }
                />
              ))}
            </List>
          </section>
        ) : null}
        <section className="sheet-section" aria-label={t('shell.links')}>
          <List>
            {extras.map((d) => (
              <li key={d.key} className="k-list__item">
                <Link className="k-list__row k-layer" to={d.path} onClick={onClose}>
                  <span className="k-list__text">
                    <span className="k-t-title-sm">{t(d.label)}</span>
                  </span>
                  <Icon icon={iconChevron} />
                </Link>
              </li>
            ))}
            <li className="k-list__item">
              <Link className="k-list__row k-layer" to="/account/sessions" onClick={onClose}>
                <span className="k-list__text">
                  <span className="k-t-title-sm">{t('nav.sessions')}</span>
                </span>
                <Icon icon={iconChevron} />
              </Link>
            </li>
            <li className="k-list__item">
              <Link className="k-list__row k-layer" to="/help" onClick={onClose}>
                <span className="k-list__text">
                  <span className="k-t-title-sm">{t('nav.help')}</span>
                </span>
                <Icon icon={iconChevron} />
              </Link>
            </li>
          </List>
        </section>
        <section className="sheet-section">
          <Segmented
            label={t('shell.theme')}
            value={mode}
            onChange={(v) => {
              const next = v as ThemeMode;
              setMode(next);
              saveThemeMode(next);
              applyThemeMode(next);
            }}
            options={[
              { value: 'device', label: t('shell.themeDevice') },
              { value: 'light', label: t('shell.themeLight') },
              { value: 'dark', label: t('shell.themeDark') },
            ]}
          />
        </section>
        <section className="sheet-section">
          <span className="k-t-label-lg">{t('shell.language')}</span>
          <Segmented
            label={t('shell.language')}
            value={lang}
            onChange={(v) => {
              const next = v as Language;
              setLang(next);
              void setLanguage(next);
            }}
            options={LANGUAGES.map((l) => ({
              value: l,
              label: l === 'en' ? 'English' : l === 'hi' ? 'हिन्दी' : 'मराठी',
            }))}
          />
        </section>
        <Button variant="outlined" size="lg" block onClick={() => setConfirmOut(true)}>
          {t('shell.signOut')}
        </Button>
      </BottomSheet>
      <Dialog
        open={confirmOut}
        onClose={() => setConfirmOut(false)}
        title={t('shell.signOutTitle')}
        actions={
          <>
            <Button variant="text" onClick={() => setConfirmOut(false)}>
              {t('app.cancel')}
            </Button>
            <Button
              destructive
              onClick={() => {
                setConfirmOut(false);
                onClose();
                void auth.signOut().then(() => navigate('/signin', { replace: true }));
              }}
            >
              {t('shell.signOut')}
            </Button>
          </>
        }
      >
        {t('shell.signOutBody')}
      </Dialog>
    </>
  );
}
